import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaClock, FaChartPie, FaBookOpen, FaRegClock, FaCalendarAlt } from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import config from '../../config';

const API_BASE_URL = `${config.API_BASE_URL}/api/study-tools`;

const SubjectTracker = () => {
  const [subjects, setSubjects] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newSubject, setNewSubject] = useState({
    name: '',
    color: '#0066cc',
    targetHours: 0,
    studyHours: 0,
    lastStudied: null
  });

  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
    '#82CA9D', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'
  ];

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      /* // Mock data for demonstration
      setTimeout(() => {
        const mockData = [
          {
            id: 1,
            name: "Mathematics",
            color: "#0088FE",
            targetHours: 20,
            studyHours: 12,
            lastStudied: "2023-04-01T10:00:00Z"
          },
          {
            id: 2,
            name: "Physics",
            color: "#00C49F",
            targetHours: 15,
            studyHours: 2,
            lastStudied: "2023-03-22T15:30:00Z"
          },
          {
            id: 3,
            name: "Computer Science",
            color: "#FFBB28",
            targetHours: 30,
            studyHours: 22,
            lastStudied: "2023-04-02T09:15:00Z"
          }
        ];
        setSubjects(mockData);
        setLoading(false);
      }, 500); */
      
      // Uncomment this section to use the real API

      const response = await axios.get(`${API_BASE_URL}/subjects`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSubjects(response.data);
      setLoading(false);

    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError('Failed to load subjects. Please try again later.');
      setLoading(false);
    }
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    try {
      // Create a new subject with a mock ID for demo
      const newId = subjects.length > 0 ? Math.max(...subjects.map(s => s.id)) + 1 : 1;
      const subjectToAdd = { 
        ...newSubject, 
        id: newId,
        lastStudied: new Date().toISOString()
      };
      
      setSubjects([...subjects, subjectToAdd]);
      setNewSubject({
        name: '',
        color: '#0066cc',
        targetHours: 0,
        studyHours: 0,
        lastStudied: null
      });
      setShowAddForm(false);
      
      // Uncomment to use real API
      /*
      const response = await axios.post(`${API_BASE_URL}/subjects`, newSubject, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSubjects([...subjects, { ...newSubject, id: response.data.id }]);
      */
    } catch (error) {
      console.error('Error creating subject:', error);
      alert('Failed to create subject. Please try again.');
    }
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setNewSubject(subject);
    setShowAddForm(true);
  };

  const handleUpdateSubject = async (e) => {
    e.preventDefault();
    try {
      // Update subject in the local state for demo
      setSubjects(subjects.map(subject =>
        subject.id === editingSubject.id ? { ...newSubject, id: subject.id } : subject
      ));
      
      setNewSubject({
        name: '',
        color: '#0066cc',
        targetHours: 0,
        studyHours: 0,
        lastStudied: null
      });
      setEditingSubject(null);
      setShowAddForm(false);
      
      // Uncomment to use real API
      /*
      await axios.put(`${API_BASE_URL}/subjects/${editingSubject.id}`, newSubject, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      */
    } catch (error) {
      console.error('Error updating subject:', error);
      alert('Failed to update subject. Please try again.');
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('Are you sure you want to delete this subject? This action cannot be undone.')) {
      return;
    }
    try {
      // Remove subject from local state for demo
      setSubjects(subjects.filter(subject => subject.id !== subjectId));
      
      // Uncomment to use real API
      /*
      await axios.delete(`${API_BASE_URL}/subjects/${subjectId}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      */
    } catch (error) {
      console.error('Error deleting subject:', error);
      alert('Failed to delete subject. Please try again.');
    }
  };

  const handleStudyTimeAdd = async (subjectId, hours) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (!subject) return;

    try {
      // Update subject in local state for demo
      setSubjects(subjects.map(s =>
        s.id === subjectId
          ? {
              ...s,
              studyHours: s.studyHours + hours,
              lastStudied: new Date().toISOString()
            }
          : s
      ));
      
      // Uncomment to use real API
      /*
      const now = new Date();
      const studySession = {
        subjectId,
        duration: hours * 60, // Convert hours to minutes
        startTime: hours > 0 ? new Date(now - hours * 60 * 60 * 1000).toISOString() : now.toISOString(),
        endTime: now.toISOString(),
        productivityScore: 100 // Default score for quick adds
      };

      await axios.post(`${API_BASE_URL}/study-sessions`, studySession, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      */
    } catch (error) {
      console.error('Error adding study time:', error);
      alert('Failed to add study time. Please try again.');
    }
  };

  const getProgress = (subject) => {
    if (!subject.targetHours) return 0;
    return Math.min(100, Math.round((subject.studyHours / subject.targetHours) * 100));
  };

  const formatLastStudied = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const pieData = subjects.map(subject => ({
    name: subject.name,
    value: subject.studyHours,
    color: subject.color
  })).filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ 
          backgroundColor: '#fff', 
          padding: '10px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <p className="label" style={{ margin: '0', fontWeight: '600' }}>{`${payload[0].name}`}</p>
          <p className="value" style={{ margin: '0' }}>{`${payload[0].value} hours`}</p>
        </div>
      );
    }
    return null;
  };

  const getTotalStudyHours = () => {
    return subjects.reduce((total, subject) => total + subject.studyHours, 0);
  };

  if (loading) {
    return (
      <div className="subject-tracker">
        <div className="loading-spinner">Loading subjects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subject-tracker">
        <div className="error-message">
          {error}
          <button onClick={fetchSubjects} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subject-tracker">
      <div className="subject-header">
        <h2><FaBookOpen /> Subject Tracker</h2>
        <button 
          className="add-subject-button"
          onClick={() => setShowAddForm(true)}
        >
          <FaPlus /> Add Subject
        </button>
      </div>

      <div className="subject-overview">
        <div className="subject-stats">
          <div className="stat-card">
            <h3>TOTAL STUDY HOURS</h3>
            <div className="stat-value">
              {getTotalStudyHours()}h
            </div>
          </div>
          
          <div className="stat-card">
            <h3>SUBJECTS</h3>
            <div className="stat-value">
              {subjects.length}
            </div>
          </div>
        </div>
        
        <div className="chart-container">
          <div className="chart-header">
            <h3>Study Time Distribution</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {pieData.length > 0 ? (
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#999' }}>
                No study data available yet.
              </div>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {showAddForm && (
        <div className="subject-form">
          <h3>{editingSubject ? 'Edit Subject' : 'Add New Subject'}</h3>
          <form onSubmit={editingSubject ? handleUpdateSubject : handleAddSubject}>
            <div className="form-group">
              <label htmlFor="name">Subject Name</label>
              <input
                type="text"
                id="name"
                value={newSubject.name}
                onChange={(e) => setNewSubject({...newSubject, name: e.target.value})}
                required
                autoFocus
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="color">Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="color"
                  id="color"
                  value={newSubject.color}
                  onChange={(e) => setNewSubject({...newSubject, color: e.target.value})}
                  style={{ width: '50px', height: '50px', padding: '0', border: 'none' }}
                />
                <span>{newSubject.color}</span>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="targetHours">Target Hours</label>
              <input
                type="number"
                id="targetHours"
                value={newSubject.targetHours}
                onChange={(e) => setNewSubject({...newSubject, targetHours: Number(e.target.value)})}
                min="0"
                required
              />
            </div>
            
            {editingSubject && (
              <div className="form-group">
                <label htmlFor="studyHours">Current Study Hours</label>
                <input
                  type="number"
                  id="studyHours"
                  value={newSubject.studyHours}
                  onChange={(e) => setNewSubject({...newSubject, studyHours: Number(e.target.value)})}
                  min="0"
                  step="0.5"
                />
              </div>
            )}
            
            <div className="form-actions">
              <button type="submit" className="submit-button">
                {editingSubject ? 'Update' : 'Add'} Subject
              </button>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingSubject(null);
                  setNewSubject({
                    name: '',
                    color: '#0066cc',
                    targetHours: 0,
                    studyHours: 0,
                    lastStudied: null
                  });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="subject-list">
        {subjects.map(subject => (
          <div className="subject-card" key={subject.id}>
            <div className="subject-info">
              <div 
                className="subject-color" 
                style={{ backgroundColor: subject.color }}
              ></div>
              <div className="subject-name">
                {subject.name}
              </div>
            </div>
            
            <div className="subject-progress">
              <div className="progress-stats">
                <div className="progress-stat">
                  <FaRegClock /> {subject.studyHours}h / {subject.targetHours}h
                </div>
                <div className="progress-percentage">
                  {getProgress(subject)}%
                </div>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${getProgress(subject)}%`,
                    backgroundColor: subject.color
                  }}
                ></div>
              </div>
            </div>
            
            <div className="subject-meta">
              <div className="last-studied">
                <FaCalendarAlt /> Last studied: {formatLastStudied(subject.lastStudied)}
              </div>
            </div>
            
            <div className="subject-actions">
              <div className="time-buttons">
                <button 
                  className="time-button add"
                  onClick={() => handleStudyTimeAdd(subject.id, 0.5)}
                >
                  +30m
                </button>
                <button 
                  className="time-button add"
                  onClick={() => handleStudyTimeAdd(subject.id, 1)}
                >
                  +1h
                </button>
                <button 
                  className="time-button add"
                  onClick={() => handleStudyTimeAdd(subject.id, 2)}
                >
                  +2h
                </button>
                <button 
                  className="time-button subtract"
                  onClick={() => handleStudyTimeAdd(subject.id, -0.5)}
                >
                  -30m
                </button>
                <button 
                  className="time-button subtract"
                  onClick={() => handleStudyTimeAdd(subject.id, -1)}
                >
                  -1h
                </button>
                <button 
                  className="time-button subtract"
                  onClick={() => handleStudyTimeAdd(subject.id, -2)}
                >
                  -2h
                </button>
              </div>
              
              <div className="action-buttons">
                <button 
                  className="action-button edit"
                  onClick={() => handleEditSubject(subject)}
                >
                  <FaEdit />
                </button>
                <button 
                  className="action-button delete"
                  onClick={() => handleDeleteSubject(subject.id)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {subjects.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <FaBookOpen />
            </div>
            <h3>No subjects yet</h3>
            <p>Add your first subject to start tracking your study time</p>
            <button 
              className="add-subject-button"
              onClick={() => setShowAddForm(true)}
            >
              <FaPlus /> Add Subject
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectTracker;